import React, { useEffect, useRef } from 'react';
import { FormattedMessage } from '../../util/reactIntl';
import { Heading } from '../../components';
import css from './ListingPage.module.css';
import { buildSpecItems } from '../../util/listingSpecs';
import SpecIcon from './SpecIcon';

/**
 * Trailer Details — iconed spec-card grid.
 *
 * All spec labels + values are real, server-rendered text (SEO-critical).
 * The card grid is a pure CSS grid of always-visible cards — no client-only
 * tabs/accordions. The optional scroll-in fade is progressive enhancement
 * (cards are fully visible in the DOM with no JS).
 *
 * Spec items are built by the shared buildSpecItems() util so the visible
 * content stays in perfect sync with the JSON-LD Product.additionalProperty
 * built in ListingPageCarousel.js.
 */
const SectionDetailsMaybe = props => {
  const { publicData, metadata = {}, listingFieldConfigs, isFieldForCategory, intl } = props;

  if (!publicData || !listingFieldConfigs) {
    return null;
  }

  const specItems = buildSpecItems({
    publicData,
    metadata,
    listingFieldConfigs,
    isFieldForCategory,
    intl,
  });

  // Progressive-enhancement: cards are fully visible in the DOM (SSR/no-JS safe);
  // this only plays a subtle staggered rise when the grid scrolls into view and
  // the user hasn't asked to reduce motion.
  const gridRef = useRef(null);
  useEffect(() => {
    const el = gridRef.current;
    if (!el || typeof IntersectionObserver === 'undefined') return undefined;
    const reduce =
      typeof window !== 'undefined' &&
      window.matchMedia &&
      window.matchMedia('(prefers-reduced-motion: reduce)').matches;
    if (reduce) return undefined;
    const obs = new IntersectionObserver(
      entries => {
        entries.forEach(entry => {
          if (entry.isIntersecting) {
            entry.target.classList.add(css.specGridInView);
            obs.disconnect();
          }
        });
      },
      { threshold: 0.15 }
    );
    obs.observe(el);
    return () => obs.disconnect();
  }, []);

  if (specItems.length === 0) {
    return null;
  }

  // SEO-rich intro line: "Specifications for this {Year} {Make} {Model} trailer."
  const introParts = [publicData.year, publicData.make, publicData.model]
    .filter(v => v != null && String(v).trim() !== '')
    .join(' ')
    .trim();
  // Avoid "... Utility Trailer trailer." when the make/model already ends with a
  // trailer-type word — use the no-suffix message variant in that case.
  const introEndsWithType = /\b(trailer|hauler|dolly)\.?$/i.test(introParts);

  return (
    <section className={css.sectionDetails}>
      <div className={css.sectionDetailsHead}>
        <Heading as="h2" rootClassName={css.sectionHeading}>
          <FormattedMessage id="ListingPage.detailsTitle" />
        </Heading>
      </div>
      {introParts ? (
        <p className={css.detailsIntro}>
          <FormattedMessage
            id={introEndsWithType ? 'ListingPage.detailsIntroNoSuffix' : 'ListingPage.detailsIntro'}
            defaultMessage={
              introEndsWithType
                ? 'Specifications for this {summary}.'
                : 'Specifications for this {summary} trailer.'
            }
            values={{ summary: introParts }}
          />
        </p>
      ) : null}

      <dl className={css.specGrid} ref={gridRef}>
        {specItems.map(({ key, label, value, iconKey }, i) => (
          <div key={key} className={css.specCard} style={{ '--i': i }}>
            <span className={css.specIcon} aria-hidden="true">
              <SpecIcon name={iconKey} />
            </span>
            <div className={css.specText}>
              <dt className={css.specLabel}>{label}</dt>
              <dd className={css.specValue}>{value}</dd>
            </div>
          </div>
        ))}
      </dl>
    </section>
  );
};

export default SectionDetailsMaybe;
